import { Button } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type {
    File as EntityFile,
    PurchaseGoods,
    PurchaseGoodsReceipt,
    PurchaseGoodsReceiptVo
} from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { CONTRACT_STATE } from "@/meta/purchase/contract";
import { RECONCILIATION_STATE, RECEIPT_STATE, RECEIPT_TYPE } from "@/meta/purchase/receipt";
import { dateFormat, dateTimeFormat, getRealyState } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { formatNumber } from "@/utils/number";
import { getShipName } from "@/utils/ship";
import { flatPurchaseGoods, type PurGoodsRow } from "@/utils/purchaseGoods";

export interface ReceiptManagementDetailData {
    purchaseGoodsReceipt?: Partial<PurchaseGoodsReceipt>;
    project?: Partial<PurchaseGoodsReceiptVo["project"]>;
    purchaseGoodsList?: PurchaseGoods[];
    file?: Partial<PurchaseGoodsReceiptVo["file"]>;
}

export interface ReceiptManagementDetailPageProps {
    receiptId?: number | string;
    loadReceiptDetail?: (id: number | string) => Promise<ReceiptManagementDetailData>;
    loadFile?: (id: number | string) => Promise<Partial<EntityFile>>;
}

/** 复用 Vue receiptService.findById 的详情接口，直接使用 request 避免装饰器影响 React 构建。 */
export async function loadReceiptManagementDetail(id: number | string): Promise<ReceiptManagementDetailData> {
    const response = await request.service.get<PurchaseGoodsReceiptVo>(
        `/admin/purchase/goods/receipt/vo/${encodeURIComponent(String(id))}`
    );
    return response.data;
}

/** 复用 Vue BaseItem 的 fileId 查询契约，按文件编号获取收货单文件资源。 */
export async function loadReceiptManagementFile(id: number | string): Promise<Partial<EntityFile>> {
    const response = await request.service.get<Partial<EntityFile>>(`/dataService/admin/file/${id}`);
    return response.data;
}

/** 详情文本沿用 Vue BaseItem 的默认占位符。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 将 Vue 的签收状态高位编码还原为状态文案。 */
function receiptStateLabel(value?: number | string): string {
    return RECEIPT_STATE.getIntroByCode(getRealyState(Number(value))) || DEFAULT_VALUE;
}

/** Vue 仅在签收完成时展示对账状态。 */
function reconciliationStateLabel(receipt?: Partial<PurchaseGoodsReceipt>): string {
    if (getRealyState(Number(receipt?.state)) !== RECEIPT_STATE.FINISHED.code) return DEFAULT_VALUE;
    return RECONCILIATION_STATE.getIntroByCode(Number(receipt?.reconciliationState)) || DEFAULT_VALUE;
}

/** 复刻 ProTable mergeSpan：相邻相同提货单的编号和申请日期只渲染首行。 */
function getRowSpan(rows: PurGoodsRow[], rowIndex: number | undefined, property: "id" | "applyDate"): number {
    if (rowIndex === undefined) return 1;
    const current = rows[rowIndex]?.[property];
    if (rowIndex > 0 && rows[rowIndex - 1]?.[property] === current) return 0;

    let span = 1;
    while (rows[rowIndex + span]?.[property] === current) span += 1;
    return span;
}

type ReceiptGoodsRow = PurGoodsRow & { rowKey: string };

/** 采购收货详情：迁移 Vue 字段、关联单据、跳转、返回和加载失败反馈。 */
export function ReceiptManagementDetailPage({
    receiptId,
    loadReceiptDetail = loadReceiptManagementDetail,
    loadFile = loadReceiptManagementFile
}: ReceiptManagementDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = receiptId ?? params.id ?? "";
    const [detail, setDetail] = useState<ReceiptManagementDetailData>();
    const [receiptFile, setReceiptFile] = useState<Partial<EntityFile>>();
    const [invalidFile, setInvalidFile] = useState<Partial<EntityFile>>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;

        void loadReceiptDetail(currentId)
            .then(value => {
                if (disposed) return;
                setDetail(value);
                // Vue BaseItem 接收 fileId 后异步读取文件详情，失败时回退显示 fileId。
                setReceiptFile(undefined);
                setInvalidFile(undefined);
                setErrorMessage(null);

                const receiptFileId = value.purchaseGoodsReceipt?.fileId ?? value.file?.id;
                if (receiptFileId !== undefined && receiptFileId !== null) {
                    void loadFile(receiptFileId)
                        .then(file => {
                            if (!disposed) setReceiptFile(file);
                        })
                        .catch(() => {
                            // 文件服务失败时保留原 fileId，不阻断详情页展示。
                        });
                }

                const invalidFileId = value.purchaseGoodsReceipt?.invalidFileId;
                if (invalidFileId !== undefined && invalidFileId !== null) {
                    void loadFile(invalidFileId)
                        .then(file => {
                            if (!disposed) setInvalidFile(file);
                        })
                        .catch(() => {
                            // 作废文件服务失败时保留原编号，确保作废信息仍可识别。
                        });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("收货详情加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [currentId, loadReceiptDetail, loadFile]);

    const receipt = detail?.purchaseGoodsReceipt;
    const goods: ReceiptGoodsRow[] = (detail?.purchaseGoodsList ? flatPurchaseGoods(detail.purchaseGoodsList) : []).map(
        (row, index) => ({ ...row, rowKey: `${row.id}-${index}` })
    );
    const realState = getRealyState(Number(receipt?.state));
    const hasProject = receipt?.projectId != null && receipt.projectName;
    const hasReconciliation =
        receipt?.reconciliationId !== undefined && receipt.reconciliationId !== null && receipt.reconciliationId !== "";
    const hasInvalidInfo = realState === CONTRACT_STATE.INVALID.code || realState === CONTRACT_STATE.INVALIDING.code;

    // 用 zhihao-ui-react 列契约承接 Vue 关联提货单的链接、船舶数量和日期展示。
    const goodsColumns: TableColumnProps<ReceiptGoodsRow>[] = [
        {
            key: "id",
            label: "提货编号",
            width: 150,
            prop: "id",
            render: ({ row }) => <Link to={`/purchasingManage/delivery/detail/${row.id}`}>{display(row.id)}</Link>
        },
        {
            key: "applyDate",
            label: "提货申请日期",
            width: 148,
            prop: "applyDate",
            render: ({ row }) => (row.applyDate ? dateFormat(row.applyDate) : DEFAULT_VALUE)
        },
        {
            key: "shipName",
            label: "船名",
            minWidth: 176,
            prop: "ship",
            render: ({ row }) => getShipName(row.ship)
        },
        {
            key: "shipTon",
            label: "提货吨数",
            minWidth: 148,
            prop: "ship",
            render: ({ row }) => formatNumber(row.ship.ton)
        },
        {
            key: "receiptQuantity",
            label: "签收吨数",
            width: 176,
            prop: "ship",
            render: ({ row }) => formatNumber(row.ship.receiptQuantity)
        },
        {
            key: "receiptDate",
            label: "签收日期",
            minWidth: 148,
            prop: "ship",
            render: ({ row }) => (row.ship.receiptDate ? dateFormat(row.ship.receiptDate) : DEFAULT_VALUE)
        }
    ];

    return (
        <section aria-label="receipt-management-detail-page" className="flex min-h-full flex-col">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(receipt?.id) }]}
                    rightInfos={[
                        {
                            label: "创建时间",
                            value: receipt?.createdTime
                                ? dateFormat(receipt.createdTime, DATETIME_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    ]}
                    stateDesc={receiptStateLabel(receipt?.state)}
                    title="签收详情"
                />

                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}

                <div className="section-title mb-4 mt-6">基本信息</div>
                <ZhGrid cols={4} gap={[20, 20]}>
                    <ZhBaseItem label="所属项目">
                        {hasProject ? (
                            <Link to={`/purchasingManage/project/info/${receipt?.projectId}`}>
                                {display(receipt?.projectName)}
                            </Link>
                        ) : (
                            display(receipt?.projectName)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="类型" value={RECEIPT_TYPE.getIntroByCode(receipt?.type) || DEFAULT_VALUE} />
                    <ZhBaseItem label="品类" value={display(receipt?.productTypeName)} />
                    <ZhBaseItem label="签收单位" value={getEnterpriseName(receipt?.receiverEnterprise)} />
                    <ZhBaseItem label="供货单位" value={display(receipt?.supplierEnterprise?.name)} />
                    <ZhBaseItem label="吨数" value={formatNumber(receipt?.quantity)} />
                    <ZhBaseItem
                        label="签收确认日期"
                        value={receipt?.receiptConfirmedDate ? dateFormat(receipt.receiptConfirmedDate) : DEFAULT_VALUE}
                    />
                    <ZhBaseItem label="对账状态" value={reconciliationStateLabel(receipt)} />
                    <ZhBaseItem label="关联对账单">
                        {hasReconciliation ? (
                            <Link to={`/purchasingManage/reconciliation/info/${receipt?.reconciliationId}`}>
                                {display(receipt?.reconciliationId)}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="单据" span={4}>
                        {receiptFile?.id ? (
                            <span className="inline-flex items-center gap-4">
                                <span>{display(receiptFile.name || receiptFile.id)}</span>
                                {receiptFile.path ? (
                                    <a href={receiptFile.path} rel="noreferrer" target="_blank">
                                        查看/下载
                                    </a>
                                ) : null}
                            </span>
                        ) : (
                            display(receipt?.fileId ?? detail?.file?.id)
                        )}
                    </ZhBaseItem>
                </ZhGrid>

                {hasInvalidInfo ? (
                    <>
                        <div className="section-title mb-4 mt-6">作废信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="文件">
                                {invalidFile?.id ? (
                                    <span className="inline-flex items-center gap-4">
                                        <span>{display(invalidFile.name || invalidFile.id)}</span>
                                        {invalidFile.path ? (
                                            <a href={invalidFile.path} rel="noreferrer" target="_blank">
                                                查看/下载
                                            </a>
                                        ) : null}
                                    </span>
                                ) : (
                                    display(receipt?.invalidFileId)
                                )}
                            </ZhBaseItem>
                            {realState === CONTRACT_STATE.INVALID.code ? (
                                <ZhBaseItem
                                    label="时间"
                                    value={receipt?.invalidTime ? dateTimeFormat(receipt.invalidTime) : DEFAULT_VALUE}
                                />
                            ) : null}
                        </ZhGrid>
                    </>
                ) : null}

                <div className="section-title mb-4 mt-6">关联提货单</div>
                <ZhTable<ReceiptGoodsRow>
                    data={goods}
                    columns={goodsColumns}
                    pagination={false}
                    rowKey="rowKey"
                    // 使用 zhihao 的 spanMethod 保留 Vue mergeSpan 对相邻提货编号和申请日期的行合并。
                    spanMethod={({ column, rowIndex }) => {
                        const property =
                            column.prop === "id" ? "id" : column.prop === "applyDate" ? "applyDate" : undefined;
                        return property ? { rowspan: getRowSpan(goods, rowIndex, property) } : undefined;
                    }}
                />

                <Button className="mt-6" onClick={() => void navigate("/purchasingManage/receiptManagement")}>
                    返回
                </Button>
            </div>
        </section>
    );
}

export default ReceiptManagementDetailPage;
